// Time-range presets for the dashboard window control.
//
// Pure logic (no React) so it's unit-testable and so the RangePicker
// component file exports only a component (keeps react-refresh happy).
//
// Every preset maps to a rolling window length in DAYS ending "now" — the
// single `period` query param the backend already accepts (1..1830, where
// 1830 = the 5-year AGT fiscal-retention horizon = "all time" ceiling).
// "This year" is calendar-year-to-date (days since Jan 1). Computed at call
// time so "today" / "year" track the clock.

import type { StringKey } from "./i18n";

export type RangePreset = "today" | "7d" | "30d" | "90d" | "year" | "all";

export const DEFAULT_RANGE_PRESET: RangePreset = "30d";

// Matches the backend PERIOD_MAX_DAYS (src/kwanza/dashboard/http/_periods.py).
export const ALL_TIME_DAYS = 1830;

const MS_PER_DAY = 86_400_000;

/** Glance-first preset order shown left→right in the picker. */
export const RANGE_PRESETS: ReadonlyArray<{
  id: RangePreset;
  labelKey: StringKey;
}> = [
  { id: "today", labelKey: "range.today" },
  { id: "7d", labelKey: "range.7d" },
  { id: "30d", labelKey: "range.30d" },
  { id: "90d", labelKey: "range.90d" },
  { id: "year", labelKey: "range.year" },
  { id: "all", labelKey: "range.all" },
];

/** Rolling window length in days for a preset (ending `now`). Clamped to
 * [1, ALL_TIME_DAYS] so it always satisfies the backend `period` bound. */
export function presetToDays(id: RangePreset, now: Date = new Date()): number {
  let days: number;
  switch (id) {
    case "today":
      days = 1;
      break;
    case "7d":
      days = 7;
      break;
    case "30d":
      days = 30;
      break;
    case "90d":
      days = 90;
      break;
    case "year": {
      const startOfYear = new Date(now.getFullYear(), 0, 1);
      days = Math.ceil((now.getTime() - startOfYear.getTime()) / MS_PER_DAY);
      break;
    }
    case "all":
      days = ALL_TIME_DAYS;
      break;
  }
  return Math.min(ALL_TIME_DAYS, Math.max(1, days));
}

/** Lower-bound (`since`) ISO-8601 timestamp for a preset's rolling window,
 * ending `now`. The Atividade feed (§13) is range-windowed by `since`; the
 * upper bound is left open (= up to now). Uses `toISOString()` so the value
 * is always Z-suffixed UTC — the backend parses ISO-8601, and Z avoids the
 * `+00:00` form whose `+` a careless query-string build would mangle. `all`
 * (the 5-year ceiling) is wide enough to be effectively unbounded. */
export function presetToSince(id: RangePreset, now: Date = new Date()): string {
  const days = presetToDays(id, now);
  return new Date(now.getTime() - days * MS_PER_DAY).toISOString();
}

export function presetLabelKey(id: RangePreset): StringKey {
  const found = RANGE_PRESETS.find((p) => p.id === id);
  // Exhaustive over RangePreset, but guard rather than throw on a stray id.
  return found ? found.labelKey : "range.30d";
}
